import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDisablePrincipal, usePrincipals } from '@/api/hooks';
import { ErrorCard } from '@/components/ErrorCard';
import { SkeletonRow } from '@/components/SkeletonRow';
import { StatusBadge } from '@/components/StatusBadge';
import { PageHeader, Panel, DangerButton, Select, Td, TextInput, Th } from './pageParts';

const MAX_PAGE_LIMIT = 200;

function pageLimitParam(searchParams: URLSearchParams, key: string, fallback: number): number {
  const parsed = Number(searchParams.get(key));
  const value = Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  return Math.min(MAX_PAGE_LIMIT, Math.max(1, Math.floor(value)));
}

function nonNegativeNumberParam(searchParams: URLSearchParams, key: string): number {
  const parsed = Number(searchParams.get(key));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function PrincipalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const tenant = searchParams.get('tenant_key') || '';
  const limit = pageLimitParam(searchParams, 'limit', 50);
  const offset = nonNegativeNumberParam(searchParams, 'offset');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const principals = usePrincipals({ q, tenant_key: tenant, limit, offset });
  const disable = useDisablePrincipal();
  const returnedCount = principals.data?.returned_count ?? 0;
  const totalCount = principals.data?.total_count ?? 0;
  const canPrevious = offset > 0;
  const canNext = offset + returnedCount < totalCount;

  function updateUrl(updates: Record<string, string | number | null>, resetOffset = false) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, String(value));
    }
    if (resetOffset) next.delete('offset');
    setSearchParams(next, { replace: true });
  }

  function onDisable(id: string) {
    const reason = window.prompt('请输入 disable reason');
    if (!reason) return;
    setInlineError(null);
    disable.mutate(
      { id, reason },
      {
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin-principals'] }),
        onError: (err) => setInlineError(err.message),
      },
    );
  }

  return (
    <div className="flex flex-col gap-panel-gap">
      <PageHeader title="Principals" subtitle="平台 principal 列表；disable 会软删除 principal、撤销 sessions 并清理派生 assignments。" />
      <Panel>
        <div className="flex flex-wrap gap-stack-md">
          <TextInput aria-label="q" value={q} onChange={(e) => updateUrl({ q: e.target.value }, true)} placeholder="display_name / email / union_id" />
          <TextInput aria-label="tenant_key" value={tenant} onChange={(e) => updateUrl({ tenant_key: e.target.value }, true)} placeholder="tenant_key" />
          <Select aria-label="limit" value={limit} onChange={(e) => updateUrl({ limit: Number(e.target.value) }, true)}><option value={50}>50</option><option value={100}>100</option><option value={200}>200</option></Select>
        </div>
      </Panel>
      {inlineError ? <ErrorCard title="Disable 被拒绝" status={disable.error?.status} message={inlineError} /> : null}
      {principals.isPending ? <Panel><SkeletonRow count={6} cells={7} /></Panel> : principals.error ? <ErrorCard title="Principals 请求失败" status={principals.error.status} body={principals.error.body} /> : (
        <Panel title={`Principals · ${returnedCount === 0 ? 0 : offset + 1}-${offset + returnedCount} / ${totalCount}`}>
          <div className="overflow-x-auto"><table className="w-full border-collapse text-left"><thead><tr className="border-b border-outline-variant bg-[#EBECF0] font-label-caps text-label-caps uppercase"><Th>principal</Th><Th>status</Th><Th>tenant_key</Th><Th>roles</Th><Th>disabled</Th><Th>action</Th></tr></thead><tbody className="divide-y divide-outline-variant font-body-sm text-body-sm">
            {(principals.data?.principals ?? []).map((p) => <tr key={p.id}><Td><div className="font-body-md text-body-md">{p.display_name}</div><div className="font-code-md text-code-md text-on-surface-variant">{p.email ?? p.id}</div></Td><Td><StatusBadge variant={p.disabled ? 'danger' : 'success'} dot>{p.disabled ? 'disabled' : 'active'}</StatusBadge></Td><Td>{p.tenant_key ?? '-'}</Td><Td>{p.platform_roles.length === 0 ? '-' : p.platform_roles.map((r) => `${r.application_id}/${r.role_id}(${r.source})`).join(', ')}</Td><Td>{p.deleted_at ?? '-'}</Td><Td><DangerButton type="button" disabled={p.disabled || disable.isPending} onClick={() => onDisable(p.id)}>Disable</DangerButton></Td></tr>)}
          </tbody></table></div>
          <div className="mt-stack-md flex items-center justify-between gap-stack-md font-body-sm text-body-sm text-on-surface-variant">
            <span>offset={offset} · limit={limit}</span>
            <div className="flex gap-stack-sm"><button type="button" disabled={!canPrevious} onClick={() => updateUrl({ offset: Math.max(0, offset - limit) })} className="rounded border border-outline-variant px-stack-md py-stack-sm disabled:opacity-50">Previous</button><button type="button" disabled={!canNext} onClick={() => updateUrl({ offset: offset + limit })} className="rounded border border-outline-variant px-stack-md py-stack-sm disabled:opacity-50">Next</button></div>
          </div>
        </Panel>
      )}
    </div>
  );
}
